import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Bed, Bath, Maximize, Mail, Phone, User, ArrowLeft, Check, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
    name: '',
    email: '',
    phone: ''
  });
  const [bookedDates, setBookedDates] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  
  useEffect(() => {
    fetchProperty();
    fetchBookedDates();
  }, [id]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
      setFormData(prev => ({
        ...prev,
        message: `I'm interested in ${response.data.title}. Please contact me with more details.`
      }));
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookedDates = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}/booked-dates`);
      setBookedDates(response.data.booked_dates || []);
    } catch (error) {
      console.error('Error fetching booked dates:', error);
      // Fall back to empty array if endpoint not available yet
      setBookedDates([]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        property_id: property.id,
        property_title: property.title,
        ...formData
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `I'm interested in ${property.title}. Please contact me with more details.`
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return `From $${price.toLocaleString()}/night`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleBookingChange = (e) => {
    setBookingDetails({
      ...bookingDetails,
      [e.target.name]: e.target.value
    });
    setCheckoutError('');
  };

  const calculateNights = () => {
    if (!bookingDetails.checkIn || !bookingDetails.checkOut) return 0;
    const start = new Date(bookingDetails.checkIn);
    const end = new Date(bookingDetails.checkOut);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  const calculateTotal = () => {
    const nights = calculateNights();
    const subtotal = nights * property.price;
    const serviceFee = subtotal * 0.1; // 10% service fee
    const taxes = subtotal * 0.08; // 8% taxes
    return {
      nights,
      subtotal,
      serviceFee,
      taxes,
      total: subtotal + serviceFee + taxes
    };
  };

  const handleStripeCheckout = async (e) => {
    e.preventDefault();
    setCheckoutLoading(true);
    setCheckoutError('');

    // Validate guest info
    if (!bookingDetails.name || !bookingDetails.email || !bookingDetails.phone) {
      setCheckoutError('Please fill in all guest information');
      setCheckoutLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/bookings/create-checkout`, {
        property_id: property.id,
        check_in: bookingDetails.checkIn,
        check_out: bookingDetails.checkOut,
        guests: parseInt(bookingDetails.guests),
        guest_name: bookingDetails.name,
        guest_email: bookingDetails.email,
        guest_phone: bookingDetails.phone,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setCheckoutError('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setCheckoutError(error.response?.data?.detail || 'Failed to process booking. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const isDateBooked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookedDates.includes(dateStr);
  };
  const getNearbyAttractions = () => {
    const cityAttractions = {
      'Sacramento': [
        { name: 'California State Capitol Museum', type: 'Landmark', distance: '2 miles' },
        { name: 'Old Sacramento Waterfront', type: 'Historic District', distance: '3 miles' },
        { name: 'Crocker Art Museum', type: 'Museum', distance: '2.5 miles' },
        { name: 'The Kitchen Restaurant', type: 'Fine Dining', distance: '1 mile' },
        { name: 'Tower Bridge', type: 'Landmark', distance: '3 miles' }
      ],
      'Los Angeles': [
        { name: 'Santa Monica Pier', type: 'Beach & Entertainment', distance: '5 miles' },
        { name: 'The Getty Center', type: 'Museum', distance: '8 miles' },
        { name: 'Griffith Observatory', type: 'Landmark', distance: '6 miles' },
        { name: 'Venice Beach Boardwalk', type: 'Beach', distance: '4 miles' },
        { name: 'Nobu Malibu', type: 'Fine Dining', distance: '10 miles' }
      ],
      'San Bernardino': [
        { name: 'Big Bear Lake', type: 'Nature & Recreation', distance: '25 miles' },
        { name: 'Lake Arrowhead', type: 'Nature', distance: '20 miles' },
        { name: 'National Orange Show Events Center', type: 'Event Venue', distance: '3 miles' },
        { name: 'Glen Helen Regional Park', type: 'Park', distance: '8 miles' },
        { name: 'Original McDonald\'s Site and Museum', type: 'Museum', distance: '4 miles' }
      ],
      'Phoenix': [
        { name: 'Desert Botanical Garden', type: 'Garden', distance: '7 miles' },
        { name: 'Camelback Mountain', type: 'Hiking', distance: '6 miles' },
        { name: 'Heard Museum', type: 'Museum', distance: '5 miles' },
        { name: 'Phoenix Zoo', type: 'Zoo', distance: '8 miles' },
        { name: 'Old Town Scottsdale', type: 'Shopping & Dining', distance: '10 miles' }
      ],
      'Scottsdale': [
        { name: 'Old Town Scottsdale', type: 'Shopping & Dining', distance: '2 miles' },
        { name: 'Taliesin West', type: 'Architecture', distance: '5 miles' },
        { name: 'McDowell Sonoran Preserve', type: 'Hiking', distance: '4 miles' },
        { name: 'Scottsdale Fashion Square', type: 'Shopping', distance: '3 miles' },
        { name: 'Desert Discovery Center', type: 'Nature Center', distance: '6 miles' }
      ],
      'Tucson': [
        { name: 'Saguaro National Park', type: 'National Park', distance: '12 miles' },
        { name: 'Arizona-Sonora Desert Museum', type: 'Museum & Zoo', distance: '14 miles' },
        { name: 'Mission San Xavier del Bac', type: 'Historic Mission', distance: '10 miles' },
        { name: 'Mount Lemmon', type: 'Mountain Recreation', distance: '25 miles' },
        { name: 'El Charro Café', type: 'Restaurant', distance: '3 miles' }
      ]
    };
    
    return cityAttractions[property.city] || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Property not found</h2>
          <Link to="/properties" className="text-blue-600 hover:text-blue-700">
            Back to Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="property-detail-page">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
            data-testid="back-to-properties"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Properties
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg mb-8">
              <div className="relative h-96">
                <img
                  src={property.images[selectedImage]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                  data-testid="main-property-image"
                />
                <div className="absolute top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full font-semibold">
                  Available Now
                </div>
                {property.featured && (
                  <div className="absolute top-4 left-4 bg-yellow-500 text-white px-4 py-2 rounded-full font-semibold">
                    Featured
                  </div>
                )}
              </div>
              {property.images.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto scrollbar-hide">
                  {property.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-2 ${
                        selectedImage === index ? 'border-blue-600' : 'border-gray-200'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img src={image} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="mb-6">
                <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="property-title">{property.title}</h1>
                <div className="flex items-center gap-2 text-gray-600 mb-6">
                  <MapPin className="h-5 w-5" />
                  <span className="text-lg">{property.location}, {property.city}, {property.state}, {property.country}</span>
                </div>
                <div className="text-4xl font-bold text-blue-600" data-testid="property-price">
                  {formatPrice(property.price)}
                </div>
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-3 gap-6 py-6 border-y border-gray-200 mb-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-900 mb-2">
                    <Bed className="h-6 w-6" />
                    <span className="text-2xl font-bold">{property.bedrooms}</span>
                  </div>
                  <p className="text-gray-600">Bedrooms</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-900 mb-2">
                    <Bath className="h-6 w-6" />
                    <span className="text-2xl font-bold">{property.bathrooms}</span>
                  </div>
                  <p className="text-gray-600">Bathrooms</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-900 mb-2">
                    <Maximize className="h-6 w-6" />
                    <span className="text-2xl font-bold">{property.sqft}</span>
                  </div>
                  <p className="text-gray-600">Square Feet</p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Property</h2>
                <p className="text-gray-700 leading-relaxed" data-testid="property-description">{property.description}</p>
              </div>

              {/* Amenities */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {property.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-700">
                      <Check className="h-5 w-5 text-green-500" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Property Type */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600 mb-1">Property Type</p>
                  <p className="text-gray-900 font-semibold capitalize">{property.property_type}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Status</p>
                  <p className="text-gray-900 font-semibold capitalize">{property.status}</p>
                </div>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cancellation Policy</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Free cancellation before 5 days of check-in</p>
                    <p className="text-gray-600 text-sm">Get a full refund if you cancel at least 5 days before your check-in date.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Partial refund after 5 days</p>
                    <p className="text-gray-600 text-sm">Cancellations within 5 days of check-in may receive a partial refund.</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-4 pt-4 border-t">
                  <span className="font-semibold">Note:</span> Review this host's full policy for details.
                </p>
              </div>
            </div>

            {/* House Rules */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">House Rules</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <svg className="h-5 w-5 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Check-in</p>
                    <p className="text-gray-600">After 3:00 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <svg className="h-5 w-5 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Check-out</p>
                    <p className="text-gray-600">Before 11:00 AM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Safety & Property */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Safety & Property</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Exterior security cameras on property</p>
                    <p className="text-gray-600 text-sm">For your safety and security, cameras monitor exterior areas.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Smoke alarm</p>
                    <p className="text-gray-600 text-sm">Smoke detectors installed throughout the property.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-gray-700 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Carbon monoxide alarm</p>
                    <p className="text-gray-600 text-sm">CO detectors ensure air quality safety.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Availability Calendar */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Availability</h2>
              <div className="border border-gray-200 rounded-lg p-4">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={prevMonth}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <h3 className="text-lg font-semibold">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h3>
                  <button
                    onClick={nextMonth}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>

                {/* Day Names */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-2">
                  {(() => {
                    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
                    const days = [];
                    
                    // Empty cells for days before month starts
                    for (let i = 0; i < startingDayOfWeek; i++) {
                      days.push(<div key={`empty-${i}`} className="aspect-square"></div>);
                    }
                    
                    // Days of the month
                    for (let day = 1; day <= daysInMonth; day++) {
                      const date = new Date(year, month, day);
                      const isBooked = isDateBooked(date);
                      const isPast = isDatePast(date);
                      
                      days.push(
                        <div
                          key={day}
                          className={`aspect-square flex items-center justify-center rounded-lg text-sm ${
                            isPast
                              ? 'text-gray-300 cursor-not-allowed'
                              : isBooked
                              ? 'bg-gray-200 text-gray-500 line-through cursor-not-allowed'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer border border-green-200'
                          }`}
                        >
                          {day}
                        </div>
                      );
                    }
                    
                    return days;
                  })()}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-6 mt-6 pt-4 border-t border-gray-200 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
                    <span className="text-gray-600">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    <span className="text-gray-600">Booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-white border border-gray-200 rounded"></div>
                    <span className="text-gray-600">Past</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Map */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Where you'll be</h2>
              <p className="text-gray-600 mb-4">
                {property.city}, {property.state}, {property.country}
              </p>
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <iframe
                  width="100%"
                  height="400"
                  frameBorder="0"
                  style={{ border: 0 }}
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8'}&q=${encodeURIComponent(property.city + ', ' + property.state)}&zoom=12`}
                  allowFullScreen
                  title="Property Location"
                ></iframe>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Exact address provided after booking
              </p>
            </div>

            {/* Things to Do Nearby */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Things to do nearby</h2>
              <p className="text-gray-600 mb-6">Explore popular attractions and restaurants near this property</p>
              <div className="space-y-4">
                {getNearbyAttractions().map((attraction, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                      <MapPin className="h-6 w-6 text-blue-900" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{attraction.name}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="bg-white px-2 py-1 rounded">{attraction.type}</span>
                        <span>• {attraction.distance}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-6 text-center">
                Distances are approximate and may vary based on traffic and route
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Agent Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 sticky top-4" data-testid="agent-card">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Reserve Your Stay</h3>
              
              {!showPaymentForm ? (
                <>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold text-gray-900">${property.price}</span>
                      <span className="text-gray-600">night</span>
                    </div>
                  </div>

                  {/* Booking Dates */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in</label>
                      <input
                        type="date"
                        name="checkIn"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        value={bookingDetails.checkIn}
                        onChange={handleBookingChange}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out</label>
                      <input
                        type="date"
                        name="checkOut"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        value={bookingDetails.checkOut}
                        onChange={handleBookingChange}
                        min={bookingDetails.checkIn || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Guests</label>
                      <select
                        name="guests"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        value={bookingDetails.guests}
                        onChange={handleBookingChange}
                      >
                        {[1,2,3,4,5,6,7,8].map(num => (
                          <option key={num} value={num}>{num} {num === 1 ? 'guest' : 'guests'}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  {bookingDetails.checkIn && bookingDetails.checkOut && calculateNights() > 0 && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>${property.price} x {calculateTotal().nights} nights</span>
                        <span>${calculateTotal().subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Service fee</span>
                        <span>${calculateTotal().serviceFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Taxes</span>
                        <span>${calculateTotal().taxes.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between font-bold text-base">
                        <span>Total</span>
                        <span>${calculateTotal().total.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setShowPaymentForm(true)}
                    disabled={!bookingDetails.checkIn || !bookingDetails.checkOut || calculateNights() <= 0}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    data-testid="continue-to-payment-btn"
                  >
                    Continue to Payment
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-4">You won't be charged yet</p>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowPaymentForm(false)}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                    data-testid="back-to-booking-btn"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to booking
                  </button>

                  <form onSubmit={handleStripeCheckout} className="space-y-4">
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">Total Amount</div>
                      <div className="text-2xl font-bold text-gray-900">${calculateTotal().total.toFixed(2)}</div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="John Doe"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        value={bookingDetails.name}
                        onChange={handleBookingChange}
                        data-testid="guest-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        value={bookingDetails.email}
                        onChange={handleBookingChange}
                        data-testid="guest-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        value={bookingDetails.phone}
                        onChange={handleBookingChange}
                        data-testid="guest-phone-input"
                      />
                    </div>

                    {checkoutError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="checkout-error">
                        {checkoutError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={checkoutLoading}
                      className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
                      data-testid="proceed-to-checkout-btn"
                    >
                      {checkoutLoading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Proceed to Secure Checkout'
                      )}
                    </button>

                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Secure checkout powered by Stripe
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      By confirming, you agree to the cancellation policy and house rules
                    </p>
                  </form>
                </>
              )}

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="font-semibold text-gray-900 text-sm mb-2">Hosted by {property.agent_name}</p>
                <div className="space-y-1 text-gray-600 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{property.agent_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{property.agent_phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;